// Client-facing planner API response shapes. Imported with `import type` by
// client components so the server-only engine is never bundled into the
// browser.

import type { ItineraryLeg, ItineraryTotals } from "./itinerary";
import type { WeatherTimelineEntry } from "./timeline";
import type { LineGeometry } from "./data";
import type { GoogleOpeningHours } from "./stops";
import type { AreaSport, Difficulty } from "./graph-types";
import type { RouteExplanation } from "./route-explanation";
import type {
  BestRunOption,
  RouteAlternative,
  StyleAlternative,
} from "./route-recommendations";
import type { DayStyle } from "./day-styles";

export type PlannerItinerary = {
  legs: ItineraryLeg[];
  totals: ItineraryTotals;
  warnings: string[];
  weatherTimeline: WeatherTimelineEntry[];
};

export type RouteSegment = { kind: ItineraryLeg["kind"]; geometry: LineGeometry | null };

export type RouteCardSummary = {
  label: "Route A" | "Route B";
  styleKey: DayStyle;
  sectorFocus: string;
  totals: ItineraryTotals;
  finishTime: string;
  liftMinutes: number;
  bestRunNames: string[];
  /** Absent on routes saved before the detailed comparison was introduced. */
  dayMetrics?: {
    trailMinutes: number;
    walkingMinutes: number;
    repeatRuns: number;
    hardestGrade: Difficulty | null;
    unratedRuns: boolean;
    finishMarginMinutes?: number;
  };
};

export type RouteComparison = {
  a: RouteCardSummary;
  b: RouteCardSummary;
  keyTradeoff: string;
  differences?: string[];
};

export type StopPin = {
  stopType: "coffee" | "lunch" | "apres";
  label: string;
  placeId: string | null;
  lat: number;
  lng: number;
  name: string | null;
  mapsUri: string | null;
  departHour: number;
};

/** A run the editor can swap a descent for, surfaced per descent leg. */
export type DescentAltOption = {
  edgeId: string; // forcedNextEdgeId to send when swapping to this run
  toNode: string;
  difficulty: Difficulty;
  name: string | null;
  aspect: string | null;
  score: number | null; // 0..10 for display; null when unavailable
  lengthM: number;
};

/** A venue the editor can swap a stop to, surfaced per stop node. */
export type VenueOption = import("./venue-policy").PlaceSourceEvidence & {
  placeId: string;
  nodeId: string;
  category: string;
  name: string | null;
  priceLevel: number | null;
  rating: number | null;
  cuisine: string | null;
  mapsUri: string | null;
  reservable: boolean | null;
  lat: number;
  lng: number;
  openingHours: GoogleOpeningHours;
};

export type ItinerarySuccess = {
  adaptiveContext?: import("./adaptive-types").AdaptivePlanContext;
  groupContext?: import("./group-journey").GroupJourneyContext;
  assessmentVersion?: number;
  selectedRoute?: "A" | "B";
  generatedAt?: string;
  evidence?: import("./route-evidence").RouteEvidence;
  preview?: boolean;
  preferences?: import("./preferences").PlannerPreferences;
  itinerary: PlannerItinerary;
  route: RouteSegment[];
  stops: StopPin[];
  scoredDate: string;
  area: { slug: string; name: string; timezone: string; availability: string };
  baseNodes: { id: string; label: string }[];
  startNodeId: string;
  editContext?: ItineraryEditContext;
  hasBuses: boolean;
  placesAvailable: boolean;
  /** Editor support: alternative runs keyed by the trail leg's index in itinerary.legs. */
  descentAlternatives: { legIndex: number; options: DescentAltOption[] }[];
  /** Editor support: venue alternatives keyed by a stop's graph node id. */
  venueOptionsByNode: Record<string, VenueOption[]>;
  routeExplanation: RouteExplanation;
  bestRunsToday: BestRunOption[];
  routeAlternatives: RouteAlternative[];
  styleAlternatives: StyleAlternative[];
  routeB?: {
    snapshot?: Omit<ItinerarySuccess, "routeB">;
    itinerary: ItinerarySuccess["itinerary"];
    route: RouteSegment[];
    comparison: RouteComparison;
  };
  /** Present when the plan was made in "next few hours" mode: how the live
   * position and clock shaped the window, for the UI's context line. */
  nowContext?: NowPlanContext;
};

export type ItineraryEditContext = {
  startNodeId: string;
  endNodeIds: string[];
  relaxedFinish: boolean;
};

export type NowPlanContext = {
  mode: "next-hours";
  /** Where the position fix snapped onto the network. */
  snappedNodeId: string;
  snapDistanceM: number;
  snapLabel: string | null;
  startTime: string; // HH:MM resort-local
  endTime: string;
  horizonHours: number;
  /** The horizon ran past lift close and was trimmed. */
  cappedByClose: boolean;
  /** A pedal-only horizon ran past usable daylight and was trimmed. */
  cappedByDaylight?: boolean;
  /** Planning before opening: the start was pushed to first lifts. */
  delayedToOpen: boolean;
};

export type ItineraryClosed = {
  closed: true;
  availability: string;
  message: string;
};

export type ItineraryResponse = ItinerarySuccess | ItineraryClosed;

export function isClosed(r: ItineraryResponse): r is ItineraryClosed {
  return "closed" in r && r.closed;
}

export type SharedPlanResponse = {
  entryLocationSlug?: string;
  planDate?: string;
  preferences?: unknown;
  payload?: { itinerary?: unknown; route?: unknown; stops?: unknown };
  sport?: unknown;
};

export function resolveSharedPlanSport(response: unknown, currentSport: AreaSport): AreaSport {
  if (
    response &&
    typeof response === "object" &&
    ((response as { sport?: unknown }).sport === "ski" ||
      (response as { sport?: unknown }).sport === "mtb")
  ) {
    return (response as { sport: AreaSport }).sport;
  }
  return currentSport;
}
